import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, Select, Textarea, toast } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import type { AudienceOut } from "../shared/types";
import { isVerificationMethod, VERIFICATION_METHODS } from "./audience-meta";
import { useSaveAudiences } from "./use-audiences-data";

const LABEL_MAX = 60;
const DESCRIPTION_MAX = 2000;
const FORM_ID = "audience-edit-form";

interface Values {
  label: string;
  description: string;
  method: string;
}

function useSchema() {
  const { t } = useTranslation();
  return z.object({
    label: z.string().trim().min(1, t("deployKnowledge.audiences.labelRequired")).max(LABEL_MAX),
    description: z.string().max(DESCRIPTION_MAX),
    method: z.string(),
  });
}

/** Rename an audience / set its description and verification method (`PUT /v1/audiences`). */
export function AudienceEditDialog({ audience, onClose }: { audience: AudienceOut; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const save = useSaveAudiences();
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(useSchema()),
    defaultValues: { label: audience.label, description: audience.description ?? "", method: audience.verification_method ?? "" },
  });
  const submit = handleSubmit((values) => {
    save.mutate(
      [
        {
          code: audience.code,
          label: values.label.trim(),
          description: values.description.trim() || null,
          icon: audience.icon,
          config: audience.config,
          verification_method: isVerificationMethod(values.method) ? values.method : null,
        },
      ],
      {
        onSuccess: () => {
          toast.success(t("deployKnowledge.audiences.saved", { name: values.label.trim() }));
          onClose();
        },
        onError: (caught) => toast.error(message(caught)),
      },
    );
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("deployKnowledge.audiences.editTitle", { name: audience.label })}
        description={t("deployKnowledge.audiences.editHint")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form={FORM_ID} loading={save.isPending}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
          <Field label={t("deployKnowledge.audiences.labelField")} error={formState.errors.label?.message}>
            <Input maxLength={LABEL_MAX} {...register("label")} />
          </Field>
          <Field label={t("deployKnowledge.audiences.descriptionField")} hint={t("deployKnowledge.audiences.descriptionHint")}>
            <Textarea rows={2} maxLength={DESCRIPTION_MAX} {...register("description")} />
          </Field>
          <Field label={t("deployKnowledge.audiences.methodField")}>
            <Select {...register("method")}>
              <option value="">{t("deployKnowledge.audiences.methodUnset")}</option>
              {VERIFICATION_METHODS.map((method) => (
                <option key={method} value={method}>
                  {t(`deployKnowledge.audiences.methods.${method}`)}
                </option>
              ))}
            </Select>
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}
